import { Button, Flex, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { RadioSelect } from '@shared/components';
import { getCardBrand, links, removeNonNumeric } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { format, parse } from 'date-fns';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  QUERY_KEY_GET_REQUEST_ALL_INFO,
  useCreateRequestPaymentByCustomerCardId,
  useCreateRequestPaymentInput,
  useGetCustomerCard,
} from '../../hooks';
import {
  CrossSellerPaymentSchema,
  ICrossSellerPaymentForm,
} from '../../schemas';
import {
  ICreateRequestPaymentByCustomerCardIdInput,
  ICreateRequestPaymentInput,
} from '../../services';
import { usePaymentState } from '../../store';

import { PaymentCreditCardAddress } from './PaymentCreditCardAddress';
import { PaymentCreditCardExistent } from './PaymentCreditCardExistent';
import { PaymentCreditCardNew } from './PaymentCreditCardNew';

export const PaymentCreditCard = () => {
  const { t } = useTranslation('cross-seller/payment');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { paymentForm, setPaymentForm } = usePaymentState();
  const companyId = sessionStorage.getItem('companyId') as string;

  const { data: customerCard } = useGetCustomerCard(companyId ?? '');

  const { mutate: createRequestPayment, isLoading } =
    useCreateRequestPaymentInput();

  const {
    mutate: createRequestPaymentByCustomerCardId,
    isLoading: isLoadingCreateRequestPaymentByCustomerCardId,
  } = useCreateRequestPaymentByCustomerCardId();

  const hookForm = useForm<ICrossSellerPaymentForm>({
    resolver: zodResolver(CrossSellerPaymentSchema()),
    shouldFocusError: false,
    defaultValues: paymentForm,
  });

  const paymentCard = hookForm.watch('paymentCard');

  const handleSubmitNewCard = (data: ICrossSellerPaymentForm) => {
    const brand = getCardBrand(data.cardNumber ?? '');
    const parsedDate = parse(data.expirationDate, 'MM/yy', new Date());
    const formattedExpirationDate = format(
      parsedDate,
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    );

    const payload: ICreateRequestPaymentInput = {
      cardNumber: removeNonNumeric(data.cardNumber ?? ''),
      confirmationNumber: data.cvv,
      expirationDate: formattedExpirationDate,
      holderName: data.name ?? '',
      holderDocument: removeNonNumeric(data.document ?? ''),
      brand,
      customerAddress: {
        number: data.number,
        street: data.street,
        district: data.district,
        city: data.city,
        state: data.state,
        postalCode: removeNonNumeric(data.postalCode),
      },
    };

    createRequestPayment(payload, {
      onSuccess: () => {
        setPaymentForm({ ...data, cvv: '' });
        queryClient.invalidateQueries(QUERY_KEY_GET_REQUEST_ALL_INFO);
        navigate(links.crossSeller.summary);
      },
    });
  };

  const handleSubmitSavedCard = (data: ICrossSellerPaymentForm) => {
    const parsedDate = parse(data.expirationDate, 'MM/yy', new Date());
    const formattedExpirationDate = format(
      parsedDate,
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    );

    const payload: ICreateRequestPaymentByCustomerCardIdInput = {
      confirmationNumber: data.cvv,
      customerCardId: data.paymentCard,
      expirationDate: formattedExpirationDate,
      companyId,
      customerAddress: {
        number: data.number,
        street: data.street,
        district: data.district,
        city: data.city,
        state: data.state,
        postalCode: removeNonNumeric(data.postalCode),
      },
    };

    createRequestPaymentByCustomerCardId(payload, {
      onSuccess: () => {
        setPaymentForm({ ...data, cvv: '' });
        queryClient.invalidateQueries(QUERY_KEY_GET_REQUEST_ALL_INFO);
        navigate(links.crossSeller.summary);
      },
    });
  };

  const handleSubmit = (data: ICrossSellerPaymentForm) => {
    if (paymentCard === 'newCard') {
      handleSubmitNewCard(data);
    } else {
      handleSubmitSavedCard(data);
    }
  };

  return (
    <FormProvider {...hookForm}>
      <form onSubmit={hookForm.handleSubmit(handleSubmit)}>
        <Flex flexDir="column" gap={4}>
          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('cross-seller/payment:myCards')}
          </Text>
          <Flex
            flexDir="column"
            gap={4}
            h={{ lg: '100dvh' }}
            maxH={{ base: 'calc(100dvh - 218px)', lg: 'calc(100dvh - 214px)' }}
            overflowY="auto"
          >
            <RadioSelect
              name="paymentCard"
              options={[
                { value: 'newCard', label: 'Novo Cartão' },
                ...customerCard?.map(card => ({
                  value: card.id,
                  label: `${getCardBrand(card.firstSixNumbers)} **** ${
                    card.lastFourNumbers
                  }`,
                })),
              ]}
            />

            {paymentCard === 'newCard' ? (
              <PaymentCreditCardNew />
            ) : (
              <PaymentCreditCardExistent />
            )}

            <PaymentCreditCardAddress />
          </Flex>

          <Flex justifyContent="space-between">
            <Button
              as={Link}
              to={links.crossSeller.plans}
              colorScheme="brand"
              variant="outline"
            >
              {t('common:previous')}
            </Button>

            <Button
              type="submit"
              colorScheme="brand"
              w={240}
              isLoading={
                hookForm.formState.isSubmitting ||
                isLoading ||
                isLoadingCreateRequestPaymentByCustomerCardId
              }
            >
              {t('common:continue')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
